// src/components/TimeOffRequest.js
import React, { useState } from "react";
import {
    TextField,
    Button,
    Box,
    Alert,
    MenuItem,
    Grid,
    FormControl,
    InputLabel,
    Select,
    Typography,
    FormHelperText,
    CircularProgress
} from "@mui/material";
import { addDoc, collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";

// Industry standard leave types
const LEAVE_TYPES = [
    { value: "Annual", label: "Annual Leave / Vacation" },
    { value: "Sick", label: "Sick Leave" },
    { value: "Personal", label: "Personal Leave" },
    { value: "Bereavement", label: "Bereavement Leave" },
    { value: "Parental", label: "Parental Leave" },
    { value: "Maternity", label: "Maternity Leave" },
    { value: "Paternity", label: "Paternity Leave" },
    { value: "Jury", label: "Jury Duty" },
    { value: "Military", label: "Military Leave" },
    { value: "Unpaid", label: "Unpaid Leave" },
    { value: "Training", label: "Training / Professional Development" },
    { value: "Other", label: "Other" }
];

const TimeOffRequest = ({ orgId, userId, userEmail }) => {
    const [formData, setFormData] = useState({
        startDate: "",
        endDate: "",
        type: "Annual",
        reason: ""
    });
    const [errors, setErrors] = useState({});
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });

        // Clear field-specific error when user makes a change
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: null
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // Check required fields
        if (!formData.startDate) newErrors.startDate = "Start date is required";
        if (!formData.endDate) newErrors.endDate = "End date is required";
        if (!formData.type) newErrors.type = "Leave type is required";

        // Validate dates
        const [startYear, startMonth, startDay] = formData.startDate.split('-');
        const startDate = new Date(parseInt(startYear), parseInt(startMonth) - 1, parseInt(startDay));

        const [endYear, endMonth, endDay] = formData.endDate.split('-');
        const endDate = new Date(parseInt(endYear), parseInt(endMonth) - 1, parseInt(endDay));
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time part for comparison

        if (startDate < today) {
            newErrors.startDate = "Start date cannot be in the past";
        }

        if (endDate < startDate) {
            newErrors.endDate = "End date must be after start date";
        }

        // If selecting "Other" type, reason is required
        if (formData.type === "Other" && !formData.reason.trim()) {
            newErrors.reason = "Please provide a reason for 'Other' leave type";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const calculateDuration = () => {
        if (!formData.startDate || !formData.endDate) return null;

        const [startYear, startMonth, startDay] = formData.startDate.split('-');
        const startDate = new Date(parseInt(startYear), parseInt(startMonth) - 1, parseInt(startDay));

        const [endYear, endMonth, endDay] = formData.endDate.split('-');
        const endDate = new Date(parseInt(endYear), parseInt(endMonth) - 1, parseInt(endDay));

        // FIX: Use correct variable names
        const diffTime = Math.abs(endDate - startDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

        return diffDays;
    };

    const validateAgainstBlockedDates = async (startDate, endDate, orgId) => {
        try {
            const blockedDatesRef = collection(db, "organizations", orgId, "blockedDates");
            const snapshot = await getDocs(blockedDatesRef);

            const blockedDates = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                startDate: doc.data().startDate?.toDate?.() || new Date(doc.data().startDate),
                endDate: doc.data().endDate?.toDate?.() || new Date(doc.data().endDate)
            }));

            for (const blockedDate of blockedDates) {
                // Check if dates overlap
                if (startDate <= blockedDate.endDate && endDate >= blockedDate.startDate) {
                    return {
                        isBlocked: true,
                        reason: blockedDate.reason,
                        type: blockedDate.type,
                        dates: `${blockedDate.startDate.toLocaleDateString()} - ${blockedDate.endDate.toLocaleDateString()}`
                    };
                }
            }

            return { isBlocked: false };
        } catch (error) {
            console.error("Error checking blocked dates:", error);
            return { isBlocked: false };
        }
    };

    const handleSubmit = async () => {
        // Reset message
        setMessage("");

        // Check for user ID
        if (!userId) {
            setMessage("❌ Error: User information is missing. Please log in again.");
            return;
        }

        // Validate form
        if (!validateForm()) {
            setMessage("❌ Please correct the errors in the form.");
            return;
        }

        setLoading(true);

        setLoading(true);

        try {
            const duration = calculateDuration();
            const [startYear, startMonth, startDay] = formData.startDate.split('-');
            const startDate = new Date(parseInt(startYear), parseInt(startMonth) - 1, parseInt(startDay));

            const [endYear, endMonth, endDay] = formData.endDate.split('-');
            const endDate = new Date(parseInt(endYear), parseInt(endMonth) - 1, parseInt(endDay));

            const blockedCheck = await validateAgainstBlockedDates(startDate, endDate, orgId);
            if (blockedCheck.isBlocked) {
                setMessage(`⛔ Cannot request time off during blocked period: ${blockedCheck.reason} (${blockedCheck.dates})`);
                setLoading(false);
                return;
            }
            // Get leave type label for display purposes
            const leaveTypeObj = LEAVE_TYPES.find(type => type.value === formData.type);
            const leaveTypeLabel = leaveTypeObj ? leaveTypeObj.label : formData.type;

            const requestData = {
                userId,
                employeeName: userEmail ? userEmail.split('@')[0] : "", // Simple name extraction
                employeeEmail: userEmail || "",
                startDate,
                endDate,
                type: formData.type,
                typeLabel: leaveTypeLabel,
                reason: formData.reason,
                duration,
                requestDate: new Date(),
                status: "pending"
            };

            // Add to Firestore
            await addDoc(collection(db, "organizations", orgId, "timeOffRequests"), requestData);

            // Reset form
            setFormData({
                startDate: "",
                endDate: "",
                type: "Annual",
                reason: ""
            });

            setMessage(`✅ Time off request for ${duration} day${duration !== 1 ? 's' : ''} submitted successfully.`);
        } catch (error) {
            console.error("Error submitting time off request:", error);
            setMessage(`❌ Error: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Calculate and show the duration
    const duration = calculateDuration();

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Request Time Off
            </Typography>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        type="date"
                        name="startDate"
                        label="Start Date"
                        value={formData.startDate}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        required
                        error={!!errors.startDate}
                        helperText={errors.startDate}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        type="date"
                        name="endDate"
                        label="End Date"
                        value={formData.endDate}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        required
                        error={!!errors.endDate}
                        helperText={errors.endDate}
                    />
                </Grid>

                {duration && (
                    <Grid item xs={12}>
                        <Alert severity="info">
                            Duration: {duration} day{duration !== 1 ? 's' : ''}
                        </Alert>
                    </Grid>
                )}

                <Grid item xs={12}>
                    <FormControl fullWidth required error={!!errors.type}>
                        <InputLabel>Type of Leave</InputLabel>
                        <Select
                            name="type"
                            value={formData.type}
                            label="Type of Leave"
                            onChange={handleChange}
                        >
                            {LEAVE_TYPES.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                    {type.label}
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.type && <FormHelperText>{errors.type}</FormHelperText>}
                    </FormControl>
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        name="reason"
                        label="Reason"
                        value={formData.reason}
                        onChange={handleChange}
                        multiline
                        rows={4}
                        placeholder="Please provide details about your time off request"
                        required={formData.type === "Other"}
                        error={!!errors.reason}
                        helperText={errors.reason || (formData.type === "Other" ? "Required for 'Other' leave type" : "")}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        disabled={loading}
                        fullWidth
                        sx={{ py: 1.5 }}
                    >
                        {loading ? <CircularProgress size={24} /> : "Submit Request"}
                    </Button>
                </Grid>

                {message && (
                    <Grid item xs={12}>
                        <Alert
                            severity={message.startsWith("✅") ? "success" : "error"}
                            sx={{ mt: 2 }}
                        >
                            {message}
                        </Alert>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default TimeOffRequest;